import Grid from '@/global/components/ui/Grid'
import { hasPermission } from '@/hooks/useBackendMenu'
import { cpfValido, isCnpjValido } from '@acme/common'
import { Divider } from '@mui/material'
import axios from 'axios'
import { FormDatePicker, FormField } from 'global/components/Form'
import { RepeatIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { match } from 'ts-pattern'
import { BrasilApiCnpj } from 'types'
import { SelectTipoDocumento } from './SelectTipoDocumento'

export function DadosEmpresa() {
    const {
        setValue,
        watch,
        setError,
        clearErrors,
        control: {
            _options: { disabled },
        },
    } = useFormContext()
    const navigate = useNavigate()

    const document = watch('documento')
    const isEdit = Boolean(watch('id'))
    const tipoDocumento = watch('tipoDocumento')

    useEffect(() => {
        const doc =
            document
                ?.toUpperCase()
                .replace(/[^A-Z0-9]/g, '')
                .slice(0, 14) || ''

        if (cpfValido(doc)) {
            clearErrors('documento')
            setValue(
                'documento',
                doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4'),
            )
        } else if (isCnpjValido(doc)) {
            axios
                .get<BrasilApiCnpj>(
                    `https://brasilapi.com.br/api/cnpj/v1/${doc}`,
                )
                .then(async ({ data }) => {
                    if (!watch('razaoSocial')) {
                        setValue('razaoSocial', data.razao_social)
                    }
                    if (!watch('nomeFantasia')) {
                        setValue('nomeFantasia', data.nome_fantasia)
                    }
                    if (!watch('uf')) {
                        setValue('uf', data.uf)
                    }
                    if (!watch('cep')) {
                        setValue('cep', data.cep)
                    }
                    if (!watch('Cidade.cidade')) {
                        setValue('Cidade', {
                            cidade: data.municipio,
                            uf: data.uf,
                            ibge: data.codigo_municipio_ibge,
                        })
                    }
                    if (!watch('bairro')) {
                        setValue('bairro', data.bairro)
                    }
                    if (!watch('endereco')) {
                        setValue('endereco', data.logradouro)
                    }
                    if (!watch('numero')) {
                        setValue('numero', data.numero)
                    }
                    if (!watch('telefoneResponsavel')) {
                        setValue(
                            'telefoneResponsavel',
                            data.ddd_telefone_1?.toString(),
                        )
                    }
                    if (!watch('telefone')) {
                        setValue('telefone', data.ddd_telefone_2?.toString())
                    }
                    if (!watch('nomeResponsavel')) {
                        setValue('nomeResponsavel', data.qsa[0].nome_socio)
                    }
                })

            clearErrors('documento')
            setValue(
                'documento',
                doc
                    .toUpperCase()
                    .replace(
                        /([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})/,
                        '$1.$2.$3/$4-$5',
                    ),
            )
        } else if (doc.length === 11) {
            setError('documento', {
                type: 'validate',
                message: 'CPF inválido',
            })
        } else if (doc.length > 11) {
            setValue(
                'documento',
                doc.replace(
                    /([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})/,
                    '$1.$2.$3/$4-$5',
                ),
            )
            setError('documento', {
                type: 'validate',
                message: 'CNPJ inválido',
            })
        }
    }, [document])

    const canChangeDiaInicioPonto =
        isEdit && hasPermission(['update', 'Empresas']) && !disabled

    return (
        <section id="dados-empresa">
            <Divider className="my-4 select-none">Dados da empresa</Divider>
            <Grid container spacing={2} columns={24}>
                <Grid item xs={24} sm={12} md={5}>
                    <SelectTipoDocumento required />
                </Grid>
                <Grid item xs={24} sm={12} md={7}>
                    <FormField
                        name="documento"
                        label="Documento"
                        required
                        placeholder={match(tipoDocumento)
                            .with('CNPJ', () => 'AB.000.ABC/0000-00')
                            .with('CPF', () => '000.000.000-00')
                            .with('CAEPF', () => '000.000.000/000-00')
                            .otherwise(() => '000000000000')}
                    />
                </Grid>
                <Grid item xs={24} sm={16} md={6}>
                    <FormDatePicker
                        name="competenciaAtual"
                        label="Competência atual"
                        required
                        type="month"
                    />
                </Grid>
                <Grid item xs={24} sm={8} md={6}>
                    <FormField
                        name="diaInicioPonto"
                        label="Dia de início do ponto"
                        required
                        type="number"
                        options={{
                            blocks: [2],
                            numericOnly: true,
                            datePattern: ['d'],
                            date: true,
                        }}
                        InputProps={{
                            readOnly: isEdit,
                            endAdornment: canChangeDiaInicioPonto && (
                                <RepeatIcon className="cursor-pointer mt-6 text-primary" />
                            ),
                        }}
                        {...(canChangeDiaInicioPonto && {
                            onClick: () => navigate('fechamento-ponto'),
                        })}
                        title="Clique para alterar"
                    />
                </Grid>
                <Grid item xs={24} md={12}>
                    <FormField
                        name="razaoSocial"
                        label="Razão social"
                        required
                    />
                </Grid>
                <Grid item xs={24} md={12}>
                    <FormField
                        name="nomeFantasia"
                        label="Nome fantasia"
                        required
                    />
                </Grid>
            </Grid>
        </section>
    )
}
